import React, { FC } from "react";
import { connect } from "react-redux";

import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { SelectChangeEvent } from "@mui/material/Select";

import TabPanel from "components/TabPanel";
import { CustomNumericFormat } from "components/FormInput";

import { ReactComponent as IconPlus } from "assets/images/icon_plus.svg";

import { ApplicationState } from "store";
import { PaymentTypeEnum, PaymentTypeResponse } from "api";
import { DiscountTypeEnum, actionCreators } from "store/Order";

import analytics from "services/analytics";
import { AnalyticsEvents } from "shared/constants/analytics";

import { getPriceFormat } from "shared/functions";

import theme from "theme";

interface ReduxProps {
  setNotes: (notes: string) => void;
  setIsPublicNotes: (displayNote: boolean) => void;
  setPaymentType: (paymentType: PaymentTypeEnum) => void;
  setNonCashPaymentType: (paymentType: PaymentTypeResponse | null) => void;
  setProvidedCashAmount: (providedAmount: number | null) => void;
  notes: string;
  isPublicNotes: boolean;
  totalAmount: number;
  providedCashAmount: number | null;
  paymentType: PaymentTypeEnum;
  nonCashPaymentType: PaymentTypeResponse | undefined;
  discountValue: number;
  discountType: DiscountTypeEnum;
  paymentTypeList: PaymentTypeResponse[];
}

interface Props {
  onPaymentTypeModalOpen: () => void;
}

const PaymentDialogContent: FC<ReduxProps & Props> = (props) => {
  const {
    totalAmount,
    providedCashAmount,
    notes,
    isPublicNotes,
    paymentType,
    nonCashPaymentType,
    discountType,
    discountValue,
    paymentTypeList,
  } = props;
  let rest = 0;

  const discountAmount =
    discountType === DiscountTypeEnum.Amount
      ? discountValue
      : Number(((totalAmount / 100) * discountValue).toFixed(2));

  const amountToPay = totalAmount - discountAmount;

  if (providedCashAmount && providedCashAmount > amountToPay) {
    rest = providedCashAmount - amountToPay;
  }

  const onChangeProvidedAmount = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { setProvidedCashAmount } = props;
    const providedAmount = parseFloat(event.target.value);
    isNaN(providedAmount)
      ? setProvidedCashAmount(null)
      : setProvidedCashAmount(providedAmount);
  };

  const onBlurProvidedAmount = (
    event: React.FocusEvent<HTMLInputElement, Element>
  ) => {
    const { setProvidedCashAmount } = props;
    const providedAmount = parseFloat(event.target.value);
    isNaN(providedAmount) && setProvidedCashAmount(0);
  };

  const onChangeNonCashPaymentType = (e: SelectChangeEvent<unknown>) => {
    const { setNonCashPaymentType, onPaymentTypeModalOpen, paymentTypeList } =
      props;
    const nonCashPaymentTypeId = e.target.value;

    if (nonCashPaymentTypeId === -1) {
      onPaymentTypeModalOpen();
      return;
    }

    const nonCashPaymentType =
      paymentTypeList.find(
        (item) => item.paymentTypeId === nonCashPaymentTypeId
      ) || null;

    setNonCashPaymentType(nonCashPaymentType);
  };

  const onChangeNote = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { setNotes } = props;
    setNotes(event.target.value);
  };

  const onChangeDisplayNote = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { setIsPublicNotes } = props;
    setIsPublicNotes(e.target.checked);
  };

  const handleTabChange = (e: React.SyntheticEvent, newValue: string) => {
    const { setPaymentType, setNonCashPaymentType, setProvidedCashAmount } =
      props;
    setPaymentType(newValue as PaymentTypeEnum);

    switch (newValue) {
      case PaymentTypeEnum.Cash:
        setNonCashPaymentType(null);
        analytics().sendEvent(AnalyticsEvents.sales_payment_type_cash);
        return;
      case PaymentTypeEnum.CreditCard:
        setProvidedCashAmount(0);
        setNonCashPaymentType(paymentTypeList[0]);
        analytics().sendEvent(AnalyticsEvents.sales_payment_type_card);
        return;
      case PaymentTypeEnum.MonoQr:
        setProvidedCashAmount(0);
        setNonCashPaymentType(null);
        analytics().sendEvent(AnalyticsEvents.sales_payment_type_mono_qr);
        return;
    }
  };

  return (
    <>
      <Tabs
        value={paymentType}
        onChange={handleTabChange}
        sx={styles.tabContainer}
        variant="fullWidth"
      >
        <Tab
          key={PaymentTypeEnum.Cash}
          value={PaymentTypeEnum.Cash}
          label="ГОТІВКА"
          disableRipple
          sx={styles.tab}
        />
        <Tab
          key={PaymentTypeEnum.CreditCard}
          value={PaymentTypeEnum.CreditCard}
          label="БЕЗГОТІВКА"
          disableRipple
          sx={styles.tab}
        />
        <Tab
          key={PaymentTypeEnum.MonoQr}
          value={PaymentTypeEnum.MonoQr}
          label="QR КАСА"
          disableRipple
          sx={styles.tab}
        />
      </Tabs>
      <TabPanel value={paymentType} index={PaymentTypeEnum.Cash}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={2}
        >
          <Typography fontWeight={100} pr={6}>
            Отримано:
          </Typography>
          <TextField
            fullWidth
            InputProps={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              inputComponent: CustomNumericFormat as any,
              inputProps: {
                fixedDecimalScale: true,
                suffix: " ₴",
                style: { textAlign: "right" },
              },
            }}
            onChange={onChangeProvidedAmount}
            onBlur={onBlurProvidedAmount}
            value={providedCashAmount}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pb={2}
        >
          <Typography fontWeight={100}>Решта:</Typography>
          <Typography fontWeight={100}>{getPriceFormat(rest)} ₴</Typography>
        </Box>
      </TabPanel>
      <TabPanel value={paymentType} index={PaymentTypeEnum.CreditCard}>
        <Box
          display="flex"
          flexDirection={"column"}
          justifyContent="space-between"
          alignItems="left"
          pb={2}
        >
          <Typography fontWeight={100} pb={1}>
            Засіб оплати:
          </Typography>
          <Select
            fullWidth
            value={nonCashPaymentType?.paymentTypeId}
            sx={{ padding: 0, height: "40px" }}
            onChange={onChangeNonCashPaymentType}
          >
            {paymentTypeList.map((item) => (
              <MenuItem key={item.paymentTypeId} value={item.paymentTypeId}>
                {item.name}
              </MenuItem>
            ))}
            <MenuItem key="add-button" value={-1} sx={{ m: 0, p: 0 }}>
              <Box display="flex" alignItems="center" m={"12px auto"}>
                <IconPlus color={"#478AFF"} />
                <Typography pl={1} color={(theme) => theme.colors.primaryBlue}>
                  Додати засіб оплати
                </Typography>
              </Box>
            </MenuItem>
          </Select>
        </Box>
      </TabPanel>
      <TabPanel value={paymentType} index={PaymentTypeEnum.MonoQr}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="left"
          pb={2}
        >
          <Typography fontWeight={100} pb={1}>
            Засіб оплати:
          </Typography>
          <Typography fontWeight={400} pb={1}>
            Платіж через інтегратора mono
          </Typography>
        </Box>
      </TabPanel>
      <Box>
        <Divider />
        <Box pt={2}>
          <TextField
            fullWidth
            multiline
            placeholder="Коментар"
            onChange={onChangeNote}
            value={notes}
          />
        </Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={1}
          pl={1}
          borderRadius={"6px"}
          bgcolor={theme.colors.blueWhite}
        >
          <Typography fontWeight={100}>Показувати коментар в чеку</Typography>
          <Switch checked={isPublicNotes} onChange={onChangeDisplayNote} />
        </Box>
      </Box>
    </>
  );
};

const styles = {
  tab: {
    color: theme.colors.secondaryBlue,
    "&.Mui-selected": {
      bgcolor: theme.colors.secondaryBlue,
      color: "white",
    },
  },
  tabContainer: {
    display: "flex",
    justifyContent: "space-between",
    border: `solid 1px ${theme.colors.primaryGrey}`,
    borderRadius: "8px",
    marginBottom: "16px",
    height: "32px",
  },
};

const mapStateToProps = ({ order, paymentType }: ApplicationState) => ({
  notes: order.notes,
  isPublicNotes: order.isPublicNotes,
  totalAmount: order.totalAmount,
  providedCashAmount: order.providedCashAmount,
  paymentType: order.paymentType,
  nonCashPaymentType: order.nonCashPaymentType || undefined,
  discountValue: order.discount.value || 0,
  discountType: order.discount.type,
  paymentTypeList: paymentType.paymentTypeList || [],
});

const mapDispatchToProps = {
  setNotes: actionCreators.setNotes,
  setIsPublicNotes: actionCreators.setIsPublicNotes,
  setPaymentType: actionCreators.setPaymentType,
  setNonCashPaymentType: actionCreators.setNonCashPaymentType,
  setProvidedCashAmount: actionCreators.setProvidedCashAmount,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PaymentDialogContent);
